import { ICreateComment } from '~/domain/ICreateComment';
import { ICreateImage } from '~/domain/ICreateImage';

import { ISidePanel, ISidePanelContext, SidePanelAction } from './types';

export const reducer = (prev: ISidePanelContext, update: { action: SidePanelAction, next?: Partial<ISidePanel> }): ISidePanelContext => {
  const { action, next } = update;

  if (action === 'tab-config' || action === 'tab-issue') {
    return { ...prev, ...next, tab: action, scrollTo: null };
  }

  if (action === 'create-issue') {
    // keeps original attachTo on first time clicked on create issue
    const isFirstTimeClickingCreate = prev.view !== 'create-issue';
    const attachTo = isFirstTimeClickingCreate ? next?.attachTo : prev.attachTo;
    const modified = isFirstTimeClickingCreate ? [] : prev.modified;
    return { ...prev, tab: 'tab-issue', view: action, attachTo, modified, issueId: null, scrollTo: null };
  }

  if (action === 'display-issue' && next?.issueId) {
    const isFirstTimeViewingIssue = prev.issueId !== next.issueId;
    // creates a new comment object when switching issues and resets modified
    const comment: ICreateComment = { content: '', images: [], issueId: next.issueId };
    const attachTo = isFirstTimeViewingIssue ? comment as unknown as Record<string, string | ICreateImage[]> : prev.attachTo;
    const modified = isFirstTimeViewingIssue ? [] : prev.modified;
    return { ...prev, tab: 'tab-issue', view: action, issueId: next.issueId, attachTo, modified, scrollTo: null };
  }

  if (action === 'list-issues') {
    return { ...prev, tab: 'tab-issue', view: action, modified: [], issueId: null, scrollTo: next?.scrollTo };
  }

  if (action === 'reset-comment' && prev.issueId) {
    const comment: ICreateComment = { content: '', images: [], issueId: prev.issueId };
    const attachTo = comment as unknown as Record<string, string | ICreateImage[]>;
    return { ...prev, attachTo, modified: [], scrollTo: next?.scrollTo };
  }

  if (action === 'attach' && prev?.attachTo?.images && next?.attachTo?.images) {
    const { attachTo: { images }, modified } = prev;
    const { attachTo: { images: appendImages } } = next;
    (images as ICreateImage[]).push(...appendImages as ICreateImage[]);
    if (!modified.find(x => x[0] === 'images')) {
      modified.push(['images', '']);
    }
    return { ...prev };
  }

  if (action === 'modify-attachTo' && prev?.attachTo && next?.attachTo) {
    const { attachTo: current, modified } = prev;
    const { attachTo: update } = next;
    // users can only update one field at the time in the UI.
    const key = Object.keys(update)[0];
    const value = update[key];
    const oldValue = current[key];
    const keyModified = modified.find(x => x[0] === key);
    let changed = false;
    if (key !== 'images') {
      // new value is equal original value
      if (keyModified && value === keyModified[1]) {
        modified.splice(modified.indexOf(keyModified), 1);
        changed = true;
      }
      if (!keyModified && value !== oldValue) {
        modified.push([key, oldValue as string]);
        changed = true;
      }
      // all text field can be modified
      current[key] = value;
    } else {
      // images can only be removed, one at the time
      // to add ui uses 'attach' action (see code above)
      const images = (current[key] as ICreateImage[]);
      images.splice(images.indexOf(value[0] as ICreateImage), 1);
      if (!images.length && keyModified) {
        modified.splice(modified.indexOf(keyModified), 1);
      }
      // we always refresh state when images changes
      // in order to remove the image from UI
      changed = true;
    }

    if (changed) {
      return { ...prev };
    }
  }

  if (action === 'reset-scrollTo') {
    return { ...prev, scrollTo: null };
  }

  return prev;
}
